import { FieldModel } from '@cibo/core'
import { ExtendedTheme } from '@cibo/ui'
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
  useTheme,
} from '@mui/material'
import { uniq } from 'ramda'
import { CSSProperties, JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useMultiselectCheckboxContext } from './MultiselectCheckboxContext'
import { MultiselectMenuItem } from './MultiselectFieldInput'

export const ANSWER_FOR_ALL = 'answerForAll'

export const MultiselectChangeAllInput = ({
  style,
  selectProps,
  optionsRenderOverride,
}: {
  style?: CSSProperties | undefined
  selectProps?: SelectProps<string[]>
  optionsRenderOverride?: ({
    field,
  }: {
    field?: FieldModel
  }) => (JSX.Element | (JSX.Element | JSX.Element[])[][] | undefined)[]
}) => {
  const {
    combinedAnswer: value = {},
    allDisabled,
    options,
    onChangeAll,
    allDetailsAreCibo,
    programEngagementFields,
    optionDisplayString,
    year,
    traitId,
  } = useMultiselectCheckboxContext()
  const theme = useTheme<ExtendedTheme>()
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')

  const disabled = options.length === 0 || allDisabled
  const immutable = programEngagementFields.data?.items.some(
    field =>
      !!field.findDetail?.({
        traitId,
        year,
      })?.immutable
  )

  const allOptions = uniq(['none', ...options])
  const displayValue = options.map(option => value[option]?.value === 'checked' && option)
  const displayStrings = displayValue.filter(val => !!val) as string[]

  return (
    <FormControl sx={{ m: 1, width: 300 }} style={style}>
      <Select<string[]>
        labelId="all-practices-input-label"
        id="all-practices-input"
        data-testid="all-practices-input"
        multiple
        value={displayStrings || []}
        onChange={(event: SelectChangeEvent<string[]>) => !allDisabled && onChangeAll(event)}
        input={<OutlinedInput />}
        label={t('selectPractice')}
        renderValue={selected =>
          selected.length
            ? uniq(selected).map(optionDisplayString).join(', ')
            : value.none?.value === 'checked'
            ? optionDisplayString('none')
            : t('selectPractice')
        }
        disabled={immutable || disabled}
        displayEmpty
        {...selectProps}
      >
        {optionsRenderOverride
          ? optionsRenderOverride({})
          : [
              Object.keys(value).map(
                answer =>
                  !!value[answer].stale && (
                    <MenuItem
                      style={{
                        backgroundColor:
                          allDetailsAreCibo && value[answer]?.value !== 'unchecked'
                            ? theme.palette.ciboSensed
                            : undefined,
                      }}
                      key={answer}
                      data-testid={`choice-${answer}`}
                      value={answer}
                    >
                      <MultiselectMenuItem
                        key={answer}
                        typeValues={{ type: 'combined' }}
                        stale={true}
                        option={answer}
                      />
                    </MenuItem>
                  )
              ),

              allOptions.map(option => (
                <MenuItem
                  style={{
                    backgroundColor:
                      allDetailsAreCibo && value[option]?.value !== 'unchecked'
                        ? theme.palette.ciboSensed
                        : undefined,
                  }}
                  key={option}
                  data-testid={`choice-${option}`}
                  value={option}
                >
                  <MultiselectMenuItem
                    key={option}
                    typeValues={{ type: 'combined' }}
                    stale={false}
                    option={option}
                  />
                </MenuItem>
              )),
            ]}
      </Select>
    </FormControl>
  )
}
